import React, { useState, useEffect } from 'react';
import { getFeature } from '@src/globalStore/featureStore';
import featureNames from '@src/utils/featureToggle/constants';
import { Card, Checkbox, Button, Modal, Select } from 'antd';
import type { FieldDef, SsdType } from './type';
import { useSsdList, useUpdateSsd } from './hooks';
import {
  SaveButton,
  StyledParagraph,
  StyledCheckboxGroup,
  HistoryTable,
  SessionTags,
  SessionTagSpace,
  SessionText,
  AdminErrorDiv,
  SsdSelectWrapper,
} from './style';
import { useSearchParams, useNavigate } from 'react-router-dom';

const columns = [
  {
    title: 'Edit Time',
    dataIndex: 'formatted_created_at',
    key: 'formatted_created_at',
    width: 200,
  },
  {
    title: 'Values',
    dataIndex: 'values',
    key: 'values',
  },
  {
    title: 'Editor',
    dataIndex: 'creator',
    key: 'creator',
    width: 220,
  },
];

const SsdPanelBody: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const ssdParam = searchParams.get('ssd_type') || ('ssd' as SsdType);

  const { data: ssdData, isLoading: ssdLoading, isError } = useSsdList(ssdParam);
  const updateSsdMutation = useUpdateSsd();

  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  const [ssdType, setSsdType] = useState<string>(ssdParam);

  useEffect(() => {
    const { fields = [] } = ssdData ?? {};
    setSelectedKeys(fields.filter((f: FieldDef) => f.selected).map((f) => f.key));
  }, [ssdData]);

  if (ssdLoading) return <div>Loading...</div>;
  if (isError) return <div>Error loading data</div>;

  const handleSave = () => {
    Modal.confirm({
      title: (
        <div style={{ margin: '0 auto', maxWidth: 600 }}>
          Save Session Source Data Configuration
        </div>
      ),
      content: (
        <div style={{ margin: '0 auto', maxWidth: 600 }}>
          <div>Field changes will be effective and visible in session source data files data</div>
          <div>generated the next day. Please confirm if you want to make these changes.</div>
        </div>
      ),
      okText: 'Confirm',
      cancelText: 'Cancel',
      width: 700,
      onOk: () => {
        setSaveLoading(true);
        updateSsdMutation.mutate(
          { selectedKeys, ssdType },
          { onSettled: () => setSaveLoading(false) },
        );
      },
    });
  };

  return (
    <Card
      title="Session Source Data Configuration"
      extra={
        <Button onClick={() => setShowHistory((v) => !v)}>
          {showHistory ? 'Hide History' : 'Show History'}
        </Button>
      }
    >
      <StyledParagraph>
        Session Source Data (SSD) is the full session level information for every play or attempted
        play in a given day
        <br />
        for your account. To learn more click{' '}
        <a
          href="/help/PLS/Session-Source-Data_1474585.html"
          target="_blank"
          rel="noopener noreferrer"
        >
          here.
        </a>
      </StyledParagraph>

      <SsdSelectWrapper>
        <Select
          defaultValue={ssdParam}
          options={ssdData.ssdOptions}
          style={{ width: 180 }}
          onChange={(value) => {
            const params = new URLSearchParams(searchParams);
            params.set('ssd_type', value);
            navigate(`${location.pathname}?${params.toString()}`, { replace: true });
            setSsdType(value);
          }}
        />
      </SsdSelectWrapper>

      <StyledCheckboxGroup
        disabled={saveLoading}
        value={selectedKeys}
        onChange={(vals) => setSelectedKeys(vals as string[])}
      >
        {ssdData?.fields.map((field: FieldDef) => {
          const isSessionTagsField = field.code === 'tg' || field.key === 'SESSION_TAGS_KEY';
          const isChecked = selectedKeys.includes(field.key);

          return (
            <div key={field.key}>
              <Checkbox value={field.key}>{field.label}</Checkbox>

              {isSessionTagsField && isChecked && (
                <>
                  {ssdData?.sessionTags?.length ? (
                    <SessionTagSpace size={[2, 2]} wrap>
                      {ssdData.sessionTags.map((t: string) => (
                        <SessionTags key={t}>{t}</SessionTags>
                      ))}
                    </SessionTagSpace>
                  ) : (
                    <SessionText>No Session Tags are currently set for this account.</SessionText>
                  )}

                  <SessionText>
                    To select the Session Tags for the Session Source Data file, please go to{' '}
                    <a href="/app/settings/session_tag" target="_blank" rel="noopener noreferrer">
                      Session Tags
                    </a>{' '}
                    page.
                  </SessionText>
                </>
              )}
            </div>
          );
        })}
      </StyledCheckboxGroup>

      <SaveButton loading={saveLoading} onClick={handleSave}>
        Save
      </SaveButton>

      {showHistory && (
        <HistoryTable
          columns={columns}
          dataSource={ssdData.history}
          pagination={false}
          rowKey="editTime"
          bordered={false}
        />
      )}
    </Card>
  );
};

// Parent: no data hooks, just the flag check
const SsdPanel: React.FC = () => {
  const ssdEnabled = getFeature(featureNames.SSD);

  if (!ssdEnabled) {
    return (
      <Card title="Session Source Data Configuration">
        <AdminErrorDiv>
          Current user does not have privilege to view and configure Session Source Data.
        </AdminErrorDiv>
      </Card>
    );
  }

  return <SsdPanelBody />;
};

export default SsdPanel;
